import { useState } from "react";
import styled from "styled-components";
import { AmplitudeUserProperties } from "@lifeat/shared/amplitude/AmplitudeUserProperties";
import { UserTypeField } from "@lifeat/shared/user/UserType";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { LifeAtMultiSelectDropdown } from "@common/LifeAtMultiSelectDropdown";
import { LifeAtSingleSelectDropdown } from "@common/LifeAtSingleSelectDropdown";
import { useUserContext } from "@contexts/UserContext";
import DesktopAppIcon from "@resources/logos/desktopApp/LifeAt_DesktopApp_MenubarIcon.svg";
import {
    updateAcquisitionSourceValue,
    updateEmotionalStateValue,
    updateHourlyRateValue,
    updateHoursLostWeeklyValue,
    updateUserTypeValue,
} from "@user/service/UserSettingsService";

const Root = styled.div`
    width: 100dvw;
    height: 100dvh;
    overflow: auto;
`;

const FormContainer = styled.div`
    padding: 64px;
    margin: 48px auto 48px auto;
    width: fit-content;
`;

const Logo = styled.div`
    height: 64px;
    width: 64px;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    background-color: black;
    border-radius: 12px;

    margin-bottom: 25px;
`;

const Title = styled.h1`
    margin-bottom: 24px;
`;

const Prompt = styled.p`
    margin-bottom: 12px;
`;

const SingleSelect = styled(LifeAtSingleSelectDropdown)`
    margin-bottom: 20px;
`;

const MultiSelect = styled(LifeAtMultiSelectDropdown)`
    margin-bottom: 20px;
`;

const NextButton = styled(LifeAtButtonV2)`
    width: 160px;
`;

const ValidationError = styled.p`
    font-size: 13px;
    color: red;
`;

type OnboardingPersonalizationProps = {
    onNext: () => void;
};

export function OnboardingPersonalization(props: OnboardingPersonalizationProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { user } = useUserContext();
    const [loading, setLoading] = useState(false);
    const [validationError, setValidationError] = useState<
        Partial<Record<"acquisitionSource" | "emotionalState" | "hoursLostWeekly" | "hourlyRate" | "userType", string>>
    >({});
    const [acquisitionSource, setAcquisitionSource] = useState<string | null>(null);
    const [emotionalState, setEmotionalState] = useState({
        stressed: { label: "I feel so stressed I can't focus", selected: false },
        anxious: { label: "I keep worrying about everything and get nothing done.", selected: false },
        sad: { label: "I feel unable to start or finish my work due to feeling blue.", selected: false },
        overwhelmed: { label: "I feel so overwhelmed it's impossible for me to plan lately.", selected: false },
        "not-sure": { label: "I am not sure.", selected: false },
    });
    const [hoursLostWeekly, setHoursLostWeekly] = useState<number | null>(null);
    const [hourlyRate, setHourlyRate] = useState<number | null>(null);
    const [userType, setUserType] = useState<string | null>(null);

    const handleNextClick = async () => {
        setLoading(true);
        try {
            const selectedEmotionalStates = Object.entries(emotionalState)
                .filter(([, { selected }]) => selected)
                .map(([key]) => key);
            if (acquisitionSource == null) {
                setValidationError({ acquisitionSource: "This field is required." });
            } else if (selectedEmotionalStates.length === 0) {
                setValidationError({ emotionalState: "Please select at least one." });
            } else if (hoursLostWeekly == null) {
                setValidationError({ hoursLostWeekly: "This field is required." });
            } else if (hourlyRate == null) {
                setValidationError({ hourlyRate: "This field is required." });
            } else if (userType == null) {
                setValidationError({ userType: "This field is required." });
            } else {
                await Promise.all([
                    updateAcquisitionSourceValue(user.uid, acquisitionSource),
                    amplitudeService.setUserProp(AmplitudeUserProperties.ACQUISITION_SOURCE, acquisitionSource),
                    updateEmotionalStateValue(user.uid, selectedEmotionalStates),
                    amplitudeService.setUserProp(AmplitudeUserProperties.EMOTIONAL_STATE, selectedEmotionalStates),
                    updateHoursLostWeeklyValue(user.uid, hoursLostWeekly),
                    amplitudeService.setUserProp(AmplitudeUserProperties.HOURS_LOST_WEEKLY, hoursLostWeekly),
                    updateHourlyRateValue(user.uid, hourlyRate),
                    amplitudeService.setUserProp(AmplitudeUserProperties.HOURLY_RATE, hourlyRate),
                    updateUserTypeValue(user.uid, userType),
                    amplitudeService.setUserProp(AmplitudeUserProperties.USER_TYPE, userType),
                ]);
                props.onNext();
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Root>
            <FormContainer>
                <Logo>
                    <DesktopAppIcon />
                </Logo>
                <Title>Welcome to LifeAt</Title>
                <Prompt>How did you hear about LifeAt?</Prompt>
                {validationError.acquisitionSource && (
                    <ValidationError>{validationError.acquisitionSource}</ValidationError>
                )}
                <SingleSelect
                    options={[
                        { label: "Friend", value: "friend" },
                        { label: "Coworker", value: "coworker" },
                        { label: "Instagram", value: "instagram" },
                        { label: "TikTok", value: "tiktok" },
                        { label: "LinkedIn", value: "linkedin" },
                        { label: "Search Engine (e.g. Google)", value: "search-engine" },
                        { label: "Other", value: "other" },
                    ]}
                    selected={acquisitionSource}
                    onChange={(value) => setAcquisitionSource(value)}
                />
                <Prompt>Which of these statements feels close to what you're feeling today?</Prompt>
                {validationError.emotionalState && <ValidationError>{validationError.emotionalState}</ValidationError>}
                <MultiSelect
                    value={emotionalState}
                    onChange={(key, selected) => {
                        setEmotionalState({
                            ...emotionalState,
                            [key]: { ...emotionalState[key], selected },
                        });
                    }}
                />
                <Prompt>Per week, how long are you distracted for or blocked by work?</Prompt>
                {validationError.hoursLostWeekly && (
                    <ValidationError>{validationError.hoursLostWeekly}</ValidationError>
                )}
                <SingleSelect
                    options={[
                        { label: "1-5 hours", value: "5" },
                        { label: "5-10 hours", value: "10" },
                        { label: "10-20 hours", value: "15" },
                        { label: "20+ hours", value: "20" },
                    ]}
                    selected={String(hoursLostWeekly)}
                    onChange={(value) => setHoursLostWeekly(Number(value))}
                />
                <Prompt>How would you value your time per hour?</Prompt>
                {validationError.hourlyRate && <ValidationError>{validationError.hourlyRate}</ValidationError>}
                <SingleSelect
                    options={[
                        { label: "$10 - $20", value: "10" },
                        { label: "$20 - $50", value: "20" },
                        { label: "$50 - $100", value: "50" },
                        { label: "$100 - $200", value: "100" },
                        { label: "$200+", value: "200" },
                    ]}
                    selected={String(hourlyRate)}
                    onChange={(value) => setHourlyRate(Number(value))}
                />
                <Prompt>Let's customize your content, music, etc. Tell us more about yourself:</Prompt>
                {validationError.userType && <ValidationError>{validationError.userType}</ValidationError>}
                <SingleSelect
                    options={[
                        { label: "Working Professional", value: UserTypeField.PROFESSIONAL_IN_PERSON },
                        { label: "College / University Student", value: UserTypeField.STUDENT_HIGHER_ED },
                        { label: "K-12 / Secondary Student", value: UserTypeField.STUDENT_COMPULSORY },
                        { label: "Other", value: UserTypeField.OTHER },
                    ]}
                    selected={userType}
                    onChange={(value) => setUserType(value)}
                />
                <NextButton isLoading={loading} variant="inverseColor" onClick={handleNextClick}>
                    Next
                </NextButton>
            </FormContainer>
        </Root>
    );
}
